import React from 'react';
import clsx from 'clsx';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  LandingHeroTopCollectorFragmentDoc,
  LandingHeroTopCollectorsFragmentDoc,
} from '@graphql/reactQuery';
import { gql } from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment LandingHeroTopCollectors on Release {
    id
    collectorsCount
    mintStartTimestamp
    artist {
      id
      userId
    }
    topCollectors {
      user {
        id
      }
      ...LandingHeroTopCollector
    }
    ...CollectModal
  }

  fragment LandingHeroTopCollector on TopReleaseCollector {
    nftsCount
    position
    user {
      id
      webappUri
      username
      publicAddress
      avatar {
        id
        url
      }
    }
  }
`);

const TopCollector = ({
  collector,
}: {
  collector: FragmentType<LandingHeroTopCollectorFragmentDoc>;
}) => {
  const { user, nftsCount, position } = getFragment(LandingHeroTopCollectorFragmentDoc, collector);
  const { id: userId, avatar, username, publicAddress, webappUri } = user;

  const trigger = (
    <div>
      <AccessibleLink
        className={clsx('relative items-center gap-3', position > 3 ? 'hidden lt:flex' : 'flex')}
        pathnameUri={webappUriPath(webappUri)}
      >
        <div className="ml-auto flex flex-col items-end gap-[2px]">
          <h4 className="font-title text-title-xxs font-medium">{username}</h4>
          <span className="text-base-xs opacity-70">
            {nftsCount} {getSingularOrPlural({ count: nftsCount, word: 'edition' })}
          </span>
        </div>
        <Avatar
          src={avatar?.url}
          size={44}
          username={username}
          walletAddress={publicAddress}
          borderRadius={44}
          webappUri={webappUri}
          disableLink
        />
        <span className="absolute right-[-6px] top-[-4px] flex h-5 w-5 items-center justify-center rounded-full bg-black font-title text-title-xxs font-medium text-white">
          {position}
        </span>
      </AccessibleLink>
    </div>
  );

  return (
    <UserHoverCard
      userHoverCardContent={<UserHoverCardContent userId={userId} />}
      trigger={trigger}
      triggerAsChild
    />
  );
};

export const TopCollectorsDesktop = ({
  release,
}: {
  release: FragmentType<LandingHeroTopCollectorsFragmentDoc>;
}) => {
  const releaseFrag = getFragment(LandingHeroTopCollectorsFragmentDoc, release);
  const collectors = releaseFrag.topCollectors;

  if (collectors.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {collectors.map(collector => (
        <TopCollector key={collector.user.id} collector={collector} />
      ))}
    </div>
  );
};
